'use strict';

const util = require('../lib/util');

/* Utility functions related to advertising which are shared across
 * multiple bootscripts, for instance phoenix-video and phoenix-ad-outstream
 */

function evalTemplate(template, model) {
    switch (template.type) {
        case 'simple':
            return model[template.key];
        // TODO: other template types
        default:
            return undefined;
    }
}

function makePageTargeting(pageTemplates, pageModel, config, pubAdsService) {
    // pubAdsService is optional and used from adServerDfp only.
    const targetingExclude =
        (config.beacons && config.beacons.targetingParams && config.beacons.targetingParams.exclude) || {};
    let pageTargeting = {};
    Object.keys(pageTemplates || {}).forEach((k) => {
        let v = evalTemplate(pageTemplates[k], pageModel);
        if (typeof v === 'number') {
            // GPT treats number 0 as not set at all, so we'll force all
            // numbers to be strings here to prevent weird issues.
            v = v + '';
        }
        if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) {
            if (pubAdsService) {
                pubAdsService.setTargeting(k, v);
            }
            if (!targetingExclude[k]) {
                pageTargeting[k] = v;
            }
        }
    });
    return pageTargeting;
}

function getReferrer() {
    var referrer;

    try {
        if (window.top.document.referrer) {
            referrer = window.top.document.referrer;
        }
    } catch (e) {
        referrer = document.referrer;
    }

    if (referrer) {
        return util.parseHostName(referrer);
    }

    return;
}

/**
 * Function to generate the pageModel. This is combined with a template to
 * generate the keyvalues that are sent to the ad server.
 * TODO: get baseModel directly from the config instead of being passed in here
 *
 * @param {object} baseModel model generated server side
 * @param {object} pageKeyValues generated client side such as 'pageOfDay'
 * @param {boolean} gdprIsApplicable If true keys will be generated from window.GdprStatus
 * @param {object} clientPageModel (optional) additional keys from the client
 * @return {object}
 */
function makePageModel(baseModel, pageKeyValues, gdprIsApplicable, clientPageModel) {
    let trackedFeaturesModel = {};
    if (window.phxTrackedFeatures) {
        trackedFeaturesModel = {
            trackedFeatures: Object.keys(window.phxTrackedFeatures)
                .map((name) => {
                    let status = window.phxTrackedFeatures[name] ? '1' : '0';
                    return name + ':' + status;
                })
                .join('|'),
        };
    }

    let gdprModel = {};

    if (gdprIsApplicable) {
        const gdpr = window.GdprStatus;
        // Sanitize page model for GDPR
        gdprModel = {
            donottrack: true,
            gdprIsApplicable: true,
            gdprIsConsentGivenPromise: gdpr.waitForConsentValue().then(() => {
                return gdpr.isConsentGivenForPurposes(gdpr.PURPOSE_IDS.advertising);
            }),
            kuid: undefined,
            optout: true,
        };
    }
    const breakpoint = util.getCurrentBreakpoint();
    return Object.assign(
        {},
        baseModel,
        trackedFeaturesModel,
        {
            correlator: window.mavenCorrelator,
            referrer: getReferrer(),
            loggedIn: window.phxIsLoggedIn() ? '1' : '0',
        },
        gdprModel,
        {
            breakpoint,
            characterCount: baseModel.characterCountByBreakpoint?.[breakpoint],
            inContentSlotCount: baseModel.inContentSlotCountByBreakpoint?.[breakpoint],
        },
        clientPageModel || {},
        pageKeyValues
    );
}

// There's a specific format required for the custom parameters
// in our outstream DFP request. See
// https://support.google.com/dfp_premium/answer/1080597
function vastCustomParams(baseParams, targeting, encode) {
    var supportedParams = [
        'ag',
        'channel',
        'correlator',
        'cs',
        'direct',
        'et',
        'experiments',
        'feats',
        'gd',
        'in',
        'lang',
        'loggedin',
        'pagetype',
        'path',
        'pageOfDay',
        'pod',
        'pt',
        'referrer',
        'terms',
        'permutive',
    ];
    if (!targeting) {
        targeting = {};
    }
    for (let key in targeting) {
        if (supportedParams.indexOf(key) > -1) {
            var val = targeting[key];
            if (Array.isArray(val)) {
                val = val.join(',');
            }
            baseParams[key] = val;
        }
    }
    return encode
        ? encodeURIComponent(
              Object.keys(baseParams)
                  .map((key) => [key, baseParams[key]].join('='))
                  .join('&')
          )
        : baseParams;
}

module.exports = {
    evalTemplate: evalTemplate,
    makePageTargeting: makePageTargeting,
    vastCustomParams: vastCustomParams,
    makePageModel: makePageModel,
    getReferrer: getReferrer,
};
